import './StateSeal.css';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';
import { Icon } from './Icon';

export type StateSealState = 'empty' | 'loading' | 'loaded' | 'error';

export interface StateSealProps {
  /** Sigla da UF (`PlaceCode`, tipo próprio: FE §4.2); "ZZ" (Exterior) desenha o globo no lugar da bandeira. */
  uf: string;
  /** Diâmetro em px: 24 (padrão) ou 40 no PlaceCard. */
  size?: number;
  /** URL da bandeira (`/flags/{uf}.webp`, §2.7.9); ausente, `null` ou com falha → sigla. */
  src?: string | null;
}

/** Bandeira circular da UF (§3.8); a sigla fica por baixo enquanto carrega e na falha. Decorativo: o nome está ao lado. */
export function StateSeal({ uf, size = 24, src = null }: StateSealProps) {
  const exterior = uf === 'ZZ';
  const url = exterior ? null : src;
  const [loadedSrc, setLoadedSrc] = useState<string | null>(null);
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const imgRef = useRef<HTMLImageElement>(null);

  useLayoutEffect(() => {
    const img = imgRef.current;
    if (img && url && img.complete && img.naturalWidth > 0) setLoadedSrc(url);
  }, [url]);

  const state: StateSealState = !url ? 'empty' : failedSrc === url ? 'error' : loadedSrc === url ? 'loaded' : 'loading';

  return (
    <span
      class={exterior ? 'state-seal state-seal--exterior' : 'state-seal'}
      data-testid="state-seal"
      data-state={state}
      aria-hidden="true"
      style={{ '--seal-size': `${size}px` }}
    >
      {exterior ? <Icon name="globe" size={Math.round(size / 2)} /> : <span class="state-seal__code">{uf}</span>}
      {url && state !== 'error' && (
        <img
          ref={imgRef}
          class="state-seal__img"
          src={url}
          width={size}
          height={size}
          alt=""
          loading="lazy"
          decoding="async"
          onLoad={() => setLoadedSrc(url)}
          onError={() => setFailedSrc(url)}
        />
      )}
    </span>
  );
}
